// @flow
import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { postVisitorLog, putVisitorLog } from '../api/visitorLogs';
// Utils
import {
  buildUtmPayload,
  clearVisitorLogStorage,
  getVisitorLogItem,
  setVisitorLogItem,
} from '../utils';
import {
  ORDER_NUMBER_KEY,
  UTM_PAYLOAD_KEY,
  VISITOR_LOG_ID_KEY,
  USER_TOKEN_KEY,
} from '../constants';
// Logger
import { logAPIException, logException } from '../../logHelper';
// Types
import type { VisitorLogUtmPayload } from '../types/VisitorLog';

export type UseUtmManagerParams = {
  userId: ?number,
  orderNumber: ?string,
  orderToken: ?string,
  enabled?: boolean,
};

export type UseUtmManagerReturn = {
  clearVisitorLogData: () => void,
  forceVisitorLogUpdate: () => void,
  getCurrentUtms: () => ?VisitorLogUtmPayload,
  getVisitorLogId: () => ?string,
};

export const useUtmManager = ({
  orderNumber,
  orderToken,
  userId,
  enabled = false,
}: UseUtmManagerParams = {}): UseUtmManagerReturn => {
  const { search } = useLocation();

  const getVisitorLogUserToken = () => {
    const userToken = getVisitorLogItem(USER_TOKEN_KEY);

    return userToken;
  };

  const getOrderNumber = () => {
    const orderNumber = getVisitorLogItem(ORDER_NUMBER_KEY);

    return orderNumber;
  };

  const handleVisitorLogUpdate = (
    visitorLogId: string,
    utmPayload: VisitorLogUtmPayload,
  ) => {
    let payload = utmPayload;

    const prevOrderNumber = getOrderNumber();

    const hasOrderNumberChanged =
      (!!(prevOrderNumber && orderToken) && prevOrderNumber !== orderNumber) ||
      (!prevOrderNumber && !!orderNumber);

    if (hasOrderNumberChanged) {
      payload = {
        ...utmPayload,
        order_number: orderNumber,
        order_token: orderToken,
      };
    }

    putVisitorLog(visitorLogId, payload)
      .then(() => {
        if (hasOrderNumberChanged) {
          setVisitorLogItem(ORDER_NUMBER_KEY, orderNumber || '');
          setVisitorLogItem(UTM_PAYLOAD_KEY, JSON.stringify(payload));
        }
      })
      .catch(e => {
        logAPIException(e, {
          message: 'Failed to relate visitor log to order',
          visitorLogId,
          orderNumber,
          orderToken,
        });
      });
  };

  const handleUserTokenUpdate = () => {
    const currentToken =
      localStorage.getItem('access-token') ||
      sessionStorage.getItem('access-token');

    const prevToken = getVisitorLogUserToken();

    const isPrevTokenInvalid = !prevToken || prevToken !== currentToken;

    const userRecentlyLoggedIn =
      isPrevTokenInvalid && !!currentToken && !!userId;

    if (userRecentlyLoggedIn) {
      const visitorLogsId = getVisitorLogItem(VISITOR_LOG_ID_KEY);

      if (visitorLogsId) {
        const visitorLogPayload = getVisitorLogItem(UTM_PAYLOAD_KEY);
        let utmPayload;
        try {
          utmPayload = visitorLogPayload
            ? JSON.parse(visitorLogPayload)
            : buildUtmPayload(search);
        } catch {
          utmPayload = buildUtmPayload(search);
        }

        if (utmPayload) {
          handleVisitorLogUpdate(visitorLogsId, utmPayload);
        }
      }
    }

    if (isPrevTokenInvalid) {
      setVisitorLogItem(USER_TOKEN_KEY, currentToken || '');
    }
  };

  const clearVisitorLogData = () => {
    clearVisitorLogStorage();
  };

  const forceVisitorLogUpdate = () => {
    const visitorLogsId = getVisitorLogItem(VISITOR_LOG_ID_KEY);

    if (visitorLogsId) {
      const visitorLogPayload = getVisitorLogItem(UTM_PAYLOAD_KEY);
      let utmPayload;

      if (!visitorLogPayload) {
        logException(new Error('Visitor log payload is missing'), {
          message: 'Failed to parse visitor log payload',
          visitorLogPayload,
          visitorLogsId,
        });
        return;
      }

      try {
        utmPayload = JSON.parse(visitorLogPayload);
      } catch (er) {
        logException(er, {
          message: 'Failed to parse visitor log payload',
          visitorLogPayload,
          visitorLogsId,
        });
      }

      if (utmPayload) {
        handleVisitorLogUpdate(visitorLogsId, utmPayload);
      }
    }
  };

  const getCurrentUtms = (): ?VisitorLogUtmPayload => {
    const visitorLogPayload = getVisitorLogItem(UTM_PAYLOAD_KEY);

    if (!visitorLogPayload) return null;

    try {
      return JSON.parse(visitorLogPayload);
    } catch (e) {
      logException(e, {
        message: 'Failed to parse current UTM payload from session',
        visitorLogPayload,
      });
      return null;
    }
  };

  const getVisitorLogId = (): ?string => getVisitorLogItem(VISITOR_LOG_ID_KEY);

  useEffect(() => {
    if (!enabled) return;

    const utmPayload = buildUtmPayload(search);
    if (!utmPayload) return;

    setVisitorLogItem(UTM_PAYLOAD_KEY, JSON.stringify(utmPayload));

    const visitorLogsId = getVisitorLogItem(VISITOR_LOG_ID_KEY);

    if (!visitorLogsId) {
      postVisitorLog(utmPayload)
        .then(data => {
          if (data?.id) {
            const newId = String(data.id);
            setVisitorLogItem(VISITOR_LOG_ID_KEY, newId);

            if (orderNumber) {
              handleVisitorLogUpdate(newId, utmPayload);
            }
          }
        })
        .catch(e => {
          logAPIException(e, {
            message: 'Failed to create visitor log with UTM payload',
            utmPayload,
          });
        });
    }
  }, []);

  useEffect(() => {
    if (!enabled) return;

    if (userId) {
      handleUserTokenUpdate();
    }
  }, [userId]);

  useEffect(() => {
    if (!enabled) return;

    const prevOrderNumber = getOrderNumber();
    const HAS_ALL_ORDER_NUMBER = prevOrderNumber && orderNumber;
    const HAS_ORDER_NUMBER_CHANGED =
      HAS_ALL_ORDER_NUMBER && prevOrderNumber !== orderNumber;

    const orderRecentlyPopulated =
      (!prevOrderNumber && !!orderNumber) || HAS_ORDER_NUMBER_CHANGED;

    if (orderRecentlyPopulated) {
      const visitorLogsId = getVisitorLogItem(VISITOR_LOG_ID_KEY);

      if (visitorLogsId) {
        const visitorLogPayload = getVisitorLogItem(UTM_PAYLOAD_KEY);
        let utmPayload;
        try {
          utmPayload = visitorLogPayload
            ? JSON.parse(visitorLogPayload)
            : buildUtmPayload(search);
        } catch {
          utmPayload = buildUtmPayload(search);
        }

        if (utmPayload) {
          handleVisitorLogUpdate(visitorLogsId, utmPayload);
        }
      }
    }
  }, [orderNumber]);

  return {
    clearVisitorLogData,
    forceVisitorLogUpdate,
    getCurrentUtms,
    getVisitorLogId,
  };
};
